import React, { useState } from 'react';
import classNames from 'classnames';
import { Icon } from 'ui';
import { addPropsIfValidChild, getComponentClassHelper } from 'ui/helpers';
import ExpandableListChild from './ExpandableListChild';

/*
  A component used to display a list of items with expandable sections.
  Usually used in sidebar contexts.

  Example usage:

  <ExpandableList expandOnInit={false}>
    <ExpandableListSection header="Expanded by Default Section" expandOnInit={true}>
      <ExpandableListChild.Base>Base Child</ExpandableListChild.Base>
      <ExpandableListChild.Link active>Link Child</ExpandableListChild.Link>
      <ExpandableListChild.Button>Button Child</ExpandableListChild.Button>
    </ExpandableListSection>
    <ExpandableListSection header="Collapse By Default Section">
      <ExpandableListChild.Base>Base Child</ExpandableListChild.Base>
      <ExpandableListChild.Link active>Link Child</ExpandableListChild.Link>
      <ExpandableListChild.Button>Button Child</ExpandableListChild.Button>
    </ExpandableListSection>
  </ExpandableList>

  * <ExpandableList> accepts the following props:
    `className` - a classname to be applied to the list container
    `expandOnInit` - a boolean value, defaulting to 'true', that determines
    whether or not each section is expanded by default

  * <ExpandableListSection> accepts the following props:
    `className` - a classname to be applied to the header container
    `header` - a string or a React component to display as the section header
*/

const componentClass = getComponentClassHelper('ExpandableList');

export const ExpandableListStaticHeader = ({
  children,
  className,
  buttonClassName,
  onClick,
  tooltip,
}) => {
  return (
    <div className={(componentClass('section-static'), className)}>
      <button
        type="button"
        className={classNames(componentClass('header'), buttonClassName)}
        onClick={onClick}
      >
        <span className={classNames(componentClass('header-label'), 'no-icon')}>
          {children}
        </span>
        {tooltip && (
          <span
            className="h-ci"
            data-toggle="tooltip"
            title={tooltip}
            data-placement="top"
          >
            <Icon name="info" className="NestedList-headerIcon c-brand-gray" />
          </span>
        )}
      </button>
    </div>
  );
};

export const ExpandableListSection = ({
  children,
  className,
  containerClassName,
  header,
  expandOnInit,
  parentExpandOnInit,
}) => {
  const [expanded, setExpanded] = useState(parentExpandOnInit || expandOnInit);
  const toggleExpanded = () => setExpanded(!expanded);

  return (
    <div className={(componentClass('section'), containerClassName)}>
      <button
        type="button"
        className={classNames(componentClass('header'), className)}
        onClick={toggleExpanded}
      >
        {children && (
          <span
            className={classNames(componentClass('header-icon'), {
              rotated: expanded,
            })}
          >
            <Icon name="triangle_down" small />
          </span>
        )}
        {typeof header === 'string' ? (
          <span className={componentClass('header-label')}>{header}</span>
        ) : (
          { header }
        )}
      </button>
      {expanded && children && (
        <ul className={componentClass('section-children')}>{children}</ul>
      )}
    </div>
  );
};

const ExpandableList = ({ children, className, expandOnInit = true }) => {
  return (
    <div className={classNames(componentClass(), className)}>
      {React.Children.map(
        children,
        addPropsIfValidChild({ parentExpandOnInit: expandOnInit }),
      )}
    </div>
  );
};

export { ExpandableListChild };
export default ExpandableList;
